import { BrandReviewStatusIcon, BrandStatusBadge } from '@/components/BrandChip';
import { BrandSearchList } from '@/components/BrandSearchList';
import { JsonViewer } from '@/components/JsonViewer';
import { TierCard } from '@/components/matching/TierCard';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { HintTooltip } from '@/components/HintTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { equipmentTypeFamiliesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type {
    Brand,
    BrandId,
    EquipmentTypeId,
    ManualDigest,
    Model,
    ModelKnownAsKind,
    ModelParkReason,
    ModelRevisitReason,
} from '@rock/types';
import { decodeCatalogModel } from '@rock/models';
import { approvedBrandAssets, pendingBrandRequestOf } from '@rock/types';
import { type ModelDocumentEntry, ownerManualDocument } from '@rock/manuals';
import { normalizeBrandName } from '@rock/util';
import {
    Archive,
    ArchiveRestore,
    Check,
    CirclePause,
    CirclePlay,
    ExternalLink,
    MoreHorizontal,
    Pencil,
    Plus,
    Trash2,
    X,
} from 'lucide-react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';
import { AcquisitionCard, type AcquisitionCardProps } from './AcquisitionCard';
import { CandidatesCard, type CandidatesCardProps } from './CandidatesCard';
import { DocumentsCard, type DocumentsCardProps } from './DocumentsCard';
import { offeredRevisions } from './documentOffers';
import { brandsById, linkedBrand } from './modelBrand';
import { liveBrandOf } from './modelJoin';
import { ModelDecodeInfo } from './ModelDecodeInfo';
import {
    BRAND_HINT,
    MergedIntoCard,
    PROVISIONAL_HINT,
    ModelNumberPanel,
    ModelTypePanel,
    SameNumberCard,
} from './ModelMatchingPanels';

const KNOWN_AS_KIND_LABELS: Record<ModelKnownAsKind, string> = {
    productName: 'Product name',
    family: 'Family / series',
    baseModel: 'Base model',
    catalogNumber: 'Catalog number',
};

const KNOWN_AS_KINDS: ModelKnownAsKind[] = ['productName', 'family', 'baseModel', 'catalogNumber'];

/** Park = a standing verdict on a REAL product (contrast Archive, the junk-identity tombstone). */
const PARK_REASONS: ModelParkReason[] = ['cant-find', 'no-manual-exists', 'unidentified'];

const PARK_REASON_LABELS: Record<ModelParkReason, string> = {
    'cant-find': 'Can’t find a manual',
    'no-manual-exists': 'No manual exists',
    unidentified: 'Can’t identify (no brand)',
};

/** What the park means for the model from here — shown under the Summary title while parked. */
const PARK_REASON_HINTS: Record<ModelParkReason, string> = {
    'cant-find':
        'Real product, a manual probably exists, nothing found it. When another home adds it or a brand is linked, Rock re-runs the search and flags it for revisit.',
    'no-manual-exists':
        'The manufacturer never published one. When another home adds it, Rock flags it for revisit without spending a search.',
    unidentified:
        'The number alone can’t be searched. Linking a brand re-runs the search; another home adding it just flags it for revisit.',
};

const REVISIT_REASON_LABELS: Record<ModelRevisitReason, string> = {
    'new-home': 'another home added this model',
    'brand-linked': 'a brand was linked',
};

export interface ModelDetailsTabProps {
    model: Model;
    /** The model's RESOLVED document entries (links joined to the shared artifacts). */
    entries: ModelDocumentEntry[];
    /** The full brand catalog — the picker's options and the brandId→name join. */
    brands: Brand[];
    saving: boolean;
    isUploading: boolean;
    uploadProgress: number;
    equipmentCount: number;
    /** Archive (junk identity — hidden from the catalog, hunt stops) or restore the model. */
    onSetArchived: (archived: boolean) => Promise<boolean>;
    /** Park the hunt on a real product with a reason, or lift the park with `null`. */
    onSetParked: (reason: ModelParkReason | null) => Promise<boolean>;
    /** Dismiss a revisit nudge — the human looked and nothing changed. */
    onDismissRevisit: () => Promise<boolean>;
    /** Link (or `null` to unlink) the model's catalog brand — authoritative, clears any suggestion. */
    onSetBrand: (brandId: BrandId | null, lineId?: string | null) => Promise<boolean>;
    /** Link the suggestion by its name through the alias projection; false when the name resolves to no brand. */
    onConfirmBrandSuggestion: (name: string) => Promise<boolean>;
    /** Dismiss the pending `brandSuggestion` without linking anything. */
    onDismissBrandSuggestion: () => Promise<boolean>;
    /** Re-file the model under another catalog type — the fix for a mis-filed model. */
    onSetType: (typeId: EquipmentTypeId) => Promise<boolean>;
    searches: AcquisitionCardProps['searches'];
    manualsJob: AcquisitionCardProps['job'];
    candidates: CandidatesCardProps['candidates'];
    onEditPublication: DocumentsCardProps['onEditPublication'];
    onUnpublish: DocumentsCardProps['onUnpublish'];
    onSupersede: DocumentsCardProps['onSupersede'];
    onAnalyzeLanguages: DocumentsCardProps['onAnalyzeLanguages'];
    onApplyTrim: DocumentsCardProps['onApplyTrim'];
    onRestoreTrim: DocumentsCardProps['onRestoreTrim'];
    onRequestSearch: AcquisitionCardProps['onRequestSearch'];
    onOfferFile: AcquisitionCardProps['onOfferFile'];
    onCancelJob: AcquisitionCardProps['onCancelJob'];
    onPublishCandidate: CandidatesCardProps['onPublish'];
    onLinkRevision: CandidatesCardProps['onLinkRevision'];
    onSetNotRelevant: CandidatesCardProps['onSetNotRelevant'];
    onBringIn: CandidatesCardProps['onBringIn'];
    onConfirmKnownAs: (index: number) => Promise<void>;
    onUnconfirmKnownAs: (index: number) => Promise<void>;
    onEditKnownAs: (index: number, name: string, kind: ModelKnownAsKind) => Promise<boolean>;
    onDeleteKnownAs: (index: number) => Promise<boolean>;
    onAddKnownAs: (entry: { name: string; kind: ModelKnownAsKind }) => Promise<boolean>;
}

/** The select's value for a model that carries no product line (a Radix Select item can't be empty). */
const NO_LINE = '__none__';

/**
 * The Details tab of the model page: summary, the document stack (Documents / Acquisition / Candidates),
 * the manual digest, Known As identities and the raw model payload on the left; on the right, the three
 * gates a unit passes to join this model (type, brand, number) and the other models under the same number.
 * Purely presentational — every write goes through a handler owned by `ModelDetailsPage`.
 */
export const ModelDetailsTab: FC<ModelDetailsTabProps> = ({
    model,
    entries,
    brands,
    saving,
    isUploading,
    uploadProgress,
    equipmentCount,
    onSetArchived,
    onSetParked,
    onDismissRevisit,
    onSetBrand,
    onConfirmBrandSuggestion,
    onDismissBrandSuggestion,
    onSetType,
    searches,
    manualsJob,
    candidates,
    onEditPublication,
    onUnpublish,
    onSupersede,
    onAnalyzeLanguages,
    onApplyTrim,
    onRestoreTrim,
    onRequestSearch,
    onOfferFile,
    onCancelJob,
    onPublishCandidate,
    onLinkRevision,
    onSetNotRelevant,
    onBringIn,
    onConfirmKnownAs,
    onUnconfirmKnownAs,
    onEditKnownAs,
    onDeleteKnownAs,
    onAddKnownAs,
}) => {
    const [addingKnownAs, setAddingKnownAs] = useState(false);
    const [archiving, setArchiving] = useState(false);
    // knownAs entries have no stable id, so editing is keyed by array index — fine for single-admin
    // use, but a concurrent write that reorders the array mid-edit could save onto the wrong entry.
    const [editingKnownAsIndex, setEditingKnownAsIndex] = useState<number | null>(null);
    const [editingKnownAsValue, setEditingKnownAsValue] = useState('');
    const [editingKnownAsKind, setEditingKnownAsKind] = useState<ModelKnownAsKind>('productName');
    const manualDigest = ownerManualDocument(entries)?.doc.digest;
    const archived = !!model.archivedAt;
    const parked = model.parked;
    const revisit = model.revisit;
    const brandLookup = useMemo(() => brandsById(brands), [brands]);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    // What the model NUMBER says the product is, when its brand uses a scheme we can read. The gate reads
    // the LINKED brand's canonical name + aliases — never `displayName`, which two different companies
    // share. An UNLINKED model still decodes, off the enrichment's free-text brand; the free text drops
    // out the moment a `brandId` exists, so a link that doesn't resolve decodes nothing rather than
    // falling back to a stale guess. `catalogBrandNames` owns that rule.
    const modelDecode = useMemo(() => decodeCatalogModel(model, linkedBrand(model, brands)), [model, brands]);
    // What the coverage forms need to know about THIS model (brand gates the whole section).
    const coverageContext = {
        brandId: model.brandId,
        spaceId: model.spaceId,
        typeId: model.typeId,
        modelCanonical: model.modelCanonical,
        modelDisplay: model.modelDisplay ?? model.modelCanonical,
    };

    const startEditKnownAs = (index: number, name: string, kind: ModelKnownAsKind) => {
        setEditingKnownAsIndex(index);
        setEditingKnownAsValue(name);
        setEditingKnownAsKind(kind);
    };

    const cancelEditKnownAs = () => {
        setEditingKnownAsIndex(null);
        setEditingKnownAsValue('');
    };

    const saveEditKnownAs = async (index: number) => {
        const name = editingKnownAsValue.trim();
        if (!name) {
            toast.error('Enter a name');
            return;
        }
        if (await onEditKnownAs(index, name, editingKnownAsKind)) cancelEditKnownAs();
    };

    return (
        <div className="grid items-start gap-6 lg:grid-cols-[2fr_1fr]">
            <div className="flex flex-col gap-6">
                <Card>
                    <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                        <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                                <CardTitle>Summary</CardTitle>
                                {archived && (
                                    <Badge
                                        variant="secondary"
                                        className="border-transparent bg-slate-200 text-slate-700"
                                    >
                                        Archived
                                    </Badge>
                                )}
                                {!archived && revisit && (
                                    <Badge
                                        variant="secondary"
                                        className="border-transparent bg-violet-100 text-violet-900"
                                    >
                                        Revisit
                                    </Badge>
                                )}
                                {!archived && parked && (
                                    <Badge
                                        variant="secondary"
                                        className="border-transparent bg-slate-200 text-slate-700"
                                    >
                                        Parked · {PARK_REASON_LABELS[parked.reason]}
                                    </Badge>
                                )}
                            </div>
                            <CardDescription>
                                {archived
                                    ? `Archived ${new Date(model.archivedAt ?? '').toLocaleString()} — hidden from the catalog; the manual hunt is off.`
                                    : revisit
                                      ? `Flagged ${new Date(revisit.at).toLocaleString()} because ${REVISIT_REASON_LABELS[revisit.reason]}. Check the Acquisition and Candidates cards, then publish, park, or dismiss.`
                                      : parked
                                        ? `Parked ${new Date(parked.at).toLocaleString()}. ${PARK_REASON_HINTS[parked.reason]}`
                                        : 'Key details from the model document.'}
                            </CardDescription>
                        </div>
                        {model.mergedInto ? null : archived ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => void onSetArchived(false)}
                                disabled={saving}
                            >
                                <ArchiveRestore className="h-4 w-4" />
                                Unarchive
                            </Button>
                        ) : (
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                {revisit && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void onDismissRevisit()}
                                        disabled={saving}
                                    >
                                        <Check className="h-4 w-4" />
                                        Dismiss revisit
                                    </Button>
                                )}
                                {parked ? (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => void onSetParked(null)}
                                        disabled={saving}
                                    >
                                        <CirclePlay className="h-4 w-4" />
                                        Unpark
                                    </Button>
                                ) : (
                                    <ParkMenu saving={saving} onPark={onSetParked} />
                                )}
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setArchiving(true)}
                                    disabled={saving}
                                >
                                    <Archive className="h-4 w-4" />
                                    Archive…
                                </Button>
                            </div>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <DetailItem
                                label="Canonical model"
                                value={
                                    <span className="inline-flex items-center gap-1">
                                        {model.modelCanonical}
                                        <ModelDecodeInfo decode={modelDecode} />
                                    </span>
                                }
                            />
                            <DetailItem label="Variant" value={model.variantKey ?? '—'} />
                            <DetailItem label="Spelling count" value={model.spellings.length} />
                            <DetailItem label="Created" value={new Date(model.createdAt).toLocaleString()} />
                            <DetailItem
                                label="Last updated"
                                value={new Date(model.updatedAt ?? model.createdAt).toLocaleString()}
                            />
                        </div>
                    </CardContent>
                </Card>

                <DocumentsCard
                    entries={entries}
                    candidates={candidates}
                    saving={saving}
                    onEditPublication={onEditPublication}
                    onUnpublish={onUnpublish}
                    onSupersede={onSupersede}
                    coverageContext={coverageContext}
                    onAnalyzeLanguages={onAnalyzeLanguages}
                    onApplyTrim={onApplyTrim}
                    onRestoreTrim={onRestoreTrim}
                />

                <AcquisitionCard
                    searches={searches}
                    job={manualsJob}
                    saving={saving}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    onRequestSearch={onRequestSearch}
                    onOfferFile={onOfferFile}
                    onCancelJob={onCancelJob}
                />

                <CandidatesCard
                    candidates={candidates}
                    modelNumber={model.modelCanonical}
                    // RESOLVED entries, not the raw link map: a dangling link (artifact write failed or
                    // still propagating) must not hide its candidate from review while Documents shows
                    // nothing — that would strand the operator with no visible handle on the document.
                    documents={Object.fromEntries(entries.map((entry) => [entry.doc.id, entry.link]))}
                    coverageContext={coverageContext}
                    saving={saving}
                    onPublish={onPublishCandidate}
                    revisions={offeredRevisions(searches, candidates)}
                    onLinkRevision={onLinkRevision}
                    onSetNotRelevant={onSetNotRelevant}
                    onBringIn={onBringIn}
                    onAnalyzeLanguages={onAnalyzeLanguages}
                    onApplyTrim={onApplyTrim}
                    onRestoreTrim={onRestoreTrim}
                />

                {manualDigest && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Manual Digest</CardTitle>
                            <CardDescription>
                                Extracted from the published owner manual — sanity-check it against the PDF.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ManualDigestPreview digest={manualDigest} />
                        </CardContent>
                    </Card>
                )}

                <Card className="group">
                    <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                        <div className="space-y-1.5">
                            <CardTitle>Known As</CardTitle>
                            <CardDescription>
                                Alternate identities of this product (marketing/family name, base model, extra catalog
                                numbers). These broaden document search and verification — confirm the ones that are
                                correct, then re-run the search to use them.
                            </CardDescription>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAddingKnownAs(true)}
                            disabled={saving}
                            className="opacity-0 max-md:opacity-100 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                        >
                            <Plus className="h-4 w-4" />
                            Add
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {(model.knownAs?.length ?? 0) === 0 ? (
                            <div className="section-description">No alternate identities recorded.</div>
                        ) : (
                            <ul className="space-y-2">
                                {model.knownAs?.map((entry, index) => {
                                    const isEditing = editingKnownAsIndex === index;
                                    return (
                                        <li
                                            key={`${entry.kind}-${entry.name}-${index}`}
                                            className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
                                        >
                                            {isEditing ? (
                                                <Input
                                                    value={editingKnownAsValue}
                                                    onChange={(event) => setEditingKnownAsValue(event.target.value)}
                                                    disabled={saving}
                                                    autoFocus
                                                    className="h-8 min-w-[200px] flex-1"
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') void saveEditKnownAs(index);
                                                        if (event.key === 'Escape') cancelEditKnownAs();
                                                    }}
                                                />
                                            ) : (
                                                <span className="break-all font-medium text-foreground">
                                                    {entry.name}
                                                </span>
                                            )}
                                            {isEditing ? (
                                                <Select
                                                    value={editingKnownAsKind}
                                                    onValueChange={(value) =>
                                                        setEditingKnownAsKind(value as ModelKnownAsKind)
                                                    }
                                                >
                                                    <SelectTrigger size="sm" className="w-auto min-w-[140px]">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {KNOWN_AS_KINDS.map((option) => (
                                                            <SelectItem key={option} value={option}>
                                                                {KNOWN_AS_KIND_LABELS[option]}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Badge variant="outline">{KNOWN_AS_KIND_LABELS[entry.kind]}</Badge>
                                            )}
                                            {entry.sourceUrl && (
                                                <a
                                                    href={entry.sourceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
                                                >
                                                    Source
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                            <div className="ml-auto flex items-center gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <Button
                                                            type="button"
                                                            variant="success"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => void saveEditKnownAs(index)}
                                                            disabled={saving}
                                                            aria-label="Save"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={cancelEditKnownAs}
                                                            disabled={saving}
                                                            aria-label="Cancel"
                                                        >
                                                            <X className="h-4 w-4 text-muted-foreground" />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {!entry.confirmed && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() =>
                                                                    startEditKnownAs(index, entry.name, entry.kind)
                                                                }
                                                                disabled={saving}
                                                                aria-label="Edit"
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        {entry.confirmed ? (
                                                            <>
                                                                <Badge className="bg-emerald-100 text-emerald-900">
                                                                    Confirmed
                                                                </Badge>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button
                                                                            type="button"
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8"
                                                                            disabled={saving}
                                                                            aria-label="More actions"
                                                                        >
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        <DropdownMenuItem
                                                                            onSelect={() =>
                                                                                void onUnconfirmKnownAs(index)
                                                                            }
                                                                        >
                                                                            Unconfirm
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                variant="success"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => void onConfirmKnownAs(index)}
                                                                disabled={saving}
                                                                aria-label="Confirm"
                                                            >
                                                                <Check className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        {!entry.confirmed && (
                                                            <Button
                                                                type="button"
                                                                variant="destructive"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => void onDeleteKnownAs(index)}
                                                                disabled={saving}
                                                                aria-label="Delete"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Model document</CardTitle>
                        <CardDescription>The complete model payload stored in Firestore.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                            <JsonViewer data={model} />
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="flex flex-col gap-4">
                {model.mergedInto && (
                    <MergedIntoCard model={model} winnerId={model.mergedInto} brandsById={brandLookup} />
                )}
                <ModelTypePanel model={model} saving={saving} onSetType={onSetType} />
                <TierCard
                    title="Brand"
                    tier={2}
                    hint={BRAND_HINT}
                    badge={<BrandReviewBadge model={model} brands={brands} />}
                >
                    <BrandDetailItem
                        model={model}
                        brands={brands}
                        saving={saving}
                        onSetBrand={onSetBrand}
                        onConfirmSuggestion={onConfirmBrandSuggestion}
                        onDismissSuggestion={onDismissBrandSuggestion}
                    />
                </TierCard>
                <ModelNumberPanel model={model} />
                <SameNumberCard model={model} brandsById={brandLookup} families={families} />
            </div>

            <AddKnownAsDialog
                open={addingKnownAs}
                onOpenChange={setAddingKnownAs}
                saving={saving}
                onSubmit={onAddKnownAs}
            />
            <ArchiveModelDialog
                open={archiving}
                onOpenChange={setArchiving}
                equipmentCount={equipmentCount}
                saving={saving}
                onArchive={() => onSetArchived(true)}
            />
        </div>
    );
};

/**
 * Park = a standing human verdict on a REAL product whose hunt shouldn't keep running: no confirmation,
 * because it hides nothing and the revisit path lifts it on its own when new information arrives. The
 * reason picker IS the action — each reason decides what a later wake-up may spend on the model.
 */
const ParkMenu: FC<{ saving: boolean; onPark: (reason: ModelParkReason) => Promise<boolean> }> = ({
    saving,
    onPark,
}) => (
    <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={saving}>
                <CirclePause className="h-4 w-4" />
                Park…
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            {PARK_REASONS.map((reason) => (
                <DropdownMenuItem key={reason} onSelect={() => void onPark(reason)}>
                    {PARK_REASON_LABELS[reason]}
                </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
    </DropdownMenu>
);

/**
 * Archive = tombstone a junk IDENTITY (mis-scanned model number, test-device artifact). Deliberately not
 * a delete: the spelling projection keeps resolving re-scans to this record — no duplicate junk, no fresh
 * paid manual hunt — and its candidates/searches stay attached for provenance.
 */
const ArchiveModelDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    equipmentCount: number;
    saving: boolean;
    onArchive: () => Promise<boolean>;
}> = ({ open, onOpenChange, equipmentCount, saving, onArchive }) => (
    <AlertDialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Archive this model?</AlertDialogTitle>
                <AlertDialogDescription>
                    For a junk identity — a mis-scanned model number or test-device artifact. It disappears from the
                    catalog and the manual hunt stops, but re-scans of the same string still resolve here instead of
                    recreating it. Reversible with Unarchive.
                    <span className="mt-2 block">
                        A real product you just can’t find a manual for? Park it instead — it stays in the catalog and
                        Rock flags it for revisit when another home adds it or a brand is linked.
                    </span>
                    {equipmentCount > 0 && (
                        <span className="mt-2 block font-medium text-destructive">
                            {equipmentCount} equipment record{equipmentCount === 1 ? '' : 's'} reference this model —
                            they keep working, but archiving is meant for identities nothing real should use.
                        </span>
                    )}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                    disabled={saving}
                    onClick={() => {
                        void onArchive().then((done) => done && onOpenChange(false));
                    }}
                >
                    Archive model
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

/** The model's brand as the join reads it: the live linked brand's status, junk, provisional, or none. */
const BrandReviewBadge: FC<{ model: Model; brands: Brand[] }> = ({ model, brands }) => {
    if (model.brandId && brands.length === 0) return null;
    const live = liveBrandOf(model.brandId, brandsById(brands));
    if (live) return <BrandStatusBadge brand={live} />;
    if (model.brandId) {
        return (
            <HintTooltip hint="Linked to a brand marked junk, or one that no longer resolves. Matching treats the model as having no brand.">
                <Badge variant="outline">junk</Badge>
            </HintTooltip>
        );
    }
    if (pendingBrandRequestOf(model)) {
        return (
            <HintTooltip hint={PROVISIONAL_HINT}>
                <Badge className="border-transparent bg-sky-100 text-sky-900">provisional</Badge>
            </HintTooltip>
        );
    }
    return (
        <HintTooltip hint="No catalog brand is linked. Any unit of this type and number can join it, and a unit naming a cataloged brand links the model to that brand. Research can only suggest one unless its sources prove it.">
            <Badge variant="outline">no brand</Badge>
        </HintTooltip>
    );
};

const DetailItem: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className="break-all text-base text-foreground">{value ?? '—'}</span>
    </div>
);

/**
 * The Brand summary slot: the linked catalog brand (authoritative, linked to its page, with its mark and
 * curation status), else the enrichment's free-text brand marked Unlinked, else em-dash. A provisional
 * model shows the brand request it waits on, and links only through that request. Any other pending
 * `brandSuggestion` is a confirm/dismiss chip: confirming links what the alias projection resolves the
 * name to, otherwise it opens the picker pre-filtered to the suggestion.
 *
 * The logo comes through `approvedBrandAssets`, the same gate the app renders behind — so what shows here
 * is exactly what a homeowner would see, and unvetted imagery on a brand not yet approved stays hidden.
 */
const BrandDetailItem: FC<{
    model: Model;
    brands: Brand[];
    saving: boolean;
    onSetBrand: (brandId: BrandId | null, lineId?: string | null) => Promise<boolean>;
    onConfirmSuggestion: (name: string) => Promise<boolean>;
    onDismissSuggestion: () => Promise<boolean>;
}> = ({ model, brands, saving, onSetBrand, onConfirmSuggestion, onDismissSuggestion }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerQuery, setPickerQuery] = useState('');
    const linked = linkedBrand(model, brands);
    const enrichedBrand = model.details?.brand || undefined;
    const pendingRequestId = pendingBrandRequestOf(model);
    const suggestion = !model.brandId && !pendingRequestId ? model.brandSuggestion : undefined;
    const logo = approvedBrandAssets(linked).logo;

    const openPicker = (query: string) => {
        setPickerQuery(query);
        setPickerOpen(true);
    };

    const confirmSuggestion = async (name: string) => {
        if (await onConfirmSuggestion(name)) return;
        // The projection has no brand for the name — hand the human the picker, pre-filtered on the
        // suffix-stripped fold so "Whirlpool Corporation" still surfaces "WHIRLPOOL".
        openPicker(normalizeBrandName(name).display || name);
    };

    return (
        <div className="flex flex-col gap-1">
            {/* The mark gets its own line — inline it stretched the summary column past its neighbours. */}
            {logo && (
                <img
                    src={logo.thumbnail}
                    alt={`${linked?.canonicalName} logo`}
                    className="h-8 w-auto max-w-[160px] self-start object-contain"
                />
            )}
            <span className="flex items-center gap-2 text-base text-foreground">
                {linked && <BrandReviewStatusIcon brand={linked} />}
                {linked ? (
                    <RouterLink
                        to={`/products/brands/${encodeURIComponent(linked.id)}`}
                        className="truncate font-medium text-primary underline-offset-4 hover:underline"
                    >
                        {linked.canonicalName}
                    </RouterLink>
                ) : (
                    // Covers both "catalog still loading" (brandId set, name resolves momentarily) and
                    // genuinely unlinked — the enrichment text beats flashing an opaque id either way.
                    <span className={cn('truncate', model.brandId && 'text-muted-foreground')}>
                        {enrichedBrand ?? '—'}
                    </span>
                )}
                {!linked && !model.brandId && !pendingRequestId && enrichedBrand && (
                    <HintTooltip hint="The brand as research or the owner wrote it. No catalog brand is linked yet, so nothing keys on this name until one is.">
                        <Badge variant="outline">Unlinked</Badge>
                    </HintTooltip>
                )}
                <HintTooltip
                    className="shrink-0 cursor-default"
                    hint={
                        pendingRequestId
                            ? "Resolve the brand request instead: linking here skips the merge into the brand's model, and units naming the requested brand would leave this model."
                            : 'Link this model to a catalog brand by hand.'
                    }
                >
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openPicker(linked?.canonicalName ?? enrichedBrand ?? '')}
                        disabled={saving || !!pendingRequestId}
                        aria-label="Edit brand"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                </HintTooltip>
            </span>
            {linked?.lines?.length ? (
                <Select
                    value={model.lineId ?? NO_LINE}
                    onValueChange={(value) => void onSetBrand(linked.id, value === NO_LINE ? null : value)}
                    disabled={saving}
                >
                    <SelectTrigger className="h-8 w-[220px]">
                        <SelectValue placeholder="Product line" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NO_LINE}>No line</SelectItem>
                        {linked.lines.map((line) => (
                            <SelectItem key={line.id} value={line.id}>
                                {line.displayName}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : null}
            {pendingRequestId && (
                <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2 text-sm">
                    <span className="text-muted-foreground">Waiting on brand request:</span>
                    <span className="font-medium">{model.brandSuggestion?.name}</span>
                    <RouterLink
                        to={`/products/brand-requests/${encodeURIComponent(pendingRequestId)}`}
                        className="ml-auto text-xs text-primary underline-offset-4 hover:underline"
                    >
                        Open request
                    </RouterLink>
                </div>
            )}
            {suggestion && (
                <div className="mt-1 flex flex-wrap items-center gap-2 rounded-md border border-dashed p-2 text-sm">
                    <span className="text-muted-foreground">Suggested brand:</span>
                    <span className="font-medium">{suggestion.name}</span>
                    {suggestion.sourceUrl && (
                        <a
                            href={suggestion.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
                        >
                            Source
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                        <Button
                            type="button"
                            variant="success"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => void confirmSuggestion(suggestion.name)}
                            disabled={saving}
                            aria-label="Confirm suggested brand"
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => void onDismissSuggestion()}
                            disabled={saving}
                            aria-label="Dismiss suggested brand"
                        >
                            <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    </div>
                </div>
            )}
            <LinkBrandDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                brands={brands}
                saving={saving}
                initialQuery={pickerQuery}
                currentBrandId={model.brandId}
                onSelect={async (brandId) => {
                    if (await onSetBrand(brandId)) setPickerOpen(false);
                }}
            />
        </div>
    );
};

const LinkBrandDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    brands: Brand[];
    saving: boolean;
    initialQuery: string;
    currentBrandId?: BrandId;
    onSelect: (brandId: BrandId | null) => Promise<void>;
}> = ({ open, onOpenChange, brands, saving, initialQuery, currentBrandId, onSelect }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Link brand</DialogTitle>
                <DialogDescription>
                    Link this model to a catalog brand. The link is authoritative — it names the model, anchors the
                    document hunt, and is never overwritten by automation.
                </DialogDescription>
            </DialogHeader>
            {/* Keyed on open so the search box re-seeds from the current brand or the suggestion each time. */}
            {open && (
                <BrandSearchList
                    brands={brands}
                    initialQuery={initialQuery}
                    currentId={currentBrandId}
                    disabled={saving}
                    emptyMessage="No brands match. A name automation met that isn’t cataloged waits in Brand Requests; add one by hand from the Brands page."
                    onSelect={(brand) => void onSelect(brand.id)}
                />
            )}
            <DialogFooter>
                {currentBrandId && (
                    <Button type="button" variant="outline" onClick={() => void onSelect(null)} disabled={saving}>
                        Unlink brand
                    </Button>
                )}
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                    Cancel
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);

const ManualDigestPreview: FC<{ digest: ManualDigest }> = ({ digest }) => (
    <div className="space-y-4 rounded-md border bg-muted/40 p-4">
        <DigestSection title="Summary">
            <div className="text-sm text-foreground">{digest.summary}</div>
        </DigestSection>

        {digest.specifications.length > 0 && (
            <DigestSection title="Specifications">
                <ul className="space-y-1 text-sm text-foreground">
                    {digest.specifications.map((spec, index) => (
                        <li key={`${spec.label}-${index}`} className="flex gap-2">
                            <span className="font-medium text-muted-foreground">{spec.label}:</span>
                            <span className="break-all">{spec.value}</span>
                            {/* Which declared spec of the type this row fills (slot-directed digests only).
                                A row WITHOUT one is a spec the manual states that the type's list lacks —
                                the cue for the expert to consider adding it. */}
                            {spec.specId && (
                                <code className="ml-auto shrink-0 text-xs text-muted-foreground">{spec.specId}</code>
                            )}
                        </li>
                    ))}
                </ul>
            </DigestSection>
        )}

        {digest.maintenance.length > 0 && (
            <DigestSection title="Maintenance">
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                    {digest.maintenance.map((item, index) => (
                        <li key={`${item.task}-${index}`}>
                            <span className="font-medium">{item.task}</span>
                            {item.interval ? ` — every ${item.interval}` : ''}
                            {item.details ? `: ${item.details}` : ''}
                        </li>
                    ))}
                </ul>
            </DigestSection>
        )}

        {digest.troubleshooting.length > 0 && (
            <DigestSection title="Troubleshooting">
                <ul className="space-y-1 text-sm text-foreground">
                    {digest.troubleshooting.map((item, index) => (
                        <li key={`${item.problem}-${index}`}>
                            <span className="font-medium">{item.problem}</span>: {item.solution}
                        </li>
                    ))}
                </ul>
            </DigestSection>
        )}

        {digest.consumables.length > 0 && (
            <DigestSection title="Consumables">
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                    {digest.consumables.map((item, index) => (
                        <li key={`${item.name}-${index}`}>
                            <span className="font-medium">{item.name}</span>
                            {item.partNumber ? ` (part ${item.partNumber})` : ''}
                            {item.spec ? ` — ${item.spec}` : ''}
                        </li>
                    ))}
                </ul>
            </DigestSection>
        )}

        {digest.safety.length > 0 && (
            <DigestSection title="Safety">
                <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                    {digest.safety.map((item, index) => (
                        <li key={`${item}-${index}`}>{item}</li>
                    ))}
                </ul>
            </DigestSection>
        )}
    </div>
);

const DigestSection: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
    <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {children}
    </div>
);

const AddKnownAsDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    saving: boolean;
    onSubmit: (entry: { name: string; kind: ModelKnownAsKind }) => Promise<boolean>;
}> = ({ open, onOpenChange, saving, onSubmit }) => {
    const [name, setName] = useState('');
    const [kind, setKind] = useState<ModelKnownAsKind>('productName');

    // Clear the form each time the dialog opens.
    useEffect(() => {
        if (open) {
            setName('');
            setKind('productName');
        }
    }, [open]);

    const submit = async () => {
        if (!name.trim()) {
            toast.error('Enter a name');
            return;
        }
        if (await onSubmit({ name, kind })) onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add an alternate identity</DialogTitle>
                    <DialogDescription>
                        Another name this product is published or sold under — a marketing/family name, a base model, or
                        an extra catalog number. Added identities are marked confirmed and broaden document search and
                        verification.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-2">
                        <Label htmlFor="known-as-name">Name</Label>
                        <Input
                            id="known-as-name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            disabled={saving}
                            placeholder="IntelliFlo3 VSF"
                            autoComplete="off"
                        />
                    </div>
                    <div className="min-w-0 space-y-2">
                        <Label htmlFor="known-as-kind">Kind</Label>
                        <Select value={kind} onValueChange={(value) => setKind(value as ModelKnownAsKind)}>
                            <SelectTrigger id="known-as-kind" className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {KNOWN_AS_KINDS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {KNOWN_AS_KIND_LABELS[option]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={saving}>
                        Add identity
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
